import router from '@/router'

function isListRoute(route) {
	return !!route?.path && route.path.endsWith('/list')
}

function isRoutePaginationTarget(vm) {
	return !!vm?.$options?.routePaginationEnabled || !!vm?.$options?._parentVnode?.data?.routerView
}

function parsePageValue(value, fallback = 1) {
	const parsedValue = Number.parseInt(value, 10)
	return Number.isFinite(parsedValue) && parsedValue > 0 ? parsedValue : fallback
}

function parsePerPageValue(value, fallback) {
	if (value === 'all') {
		return fallback === 'all' ? 'all' : fallback
	}

	const parsedValue = Number.parseInt(value, 10)
	return Number.isFinite(parsedValue) && parsedValue > 0 ? parsedValue : fallback
}

function getBindings(vm, source) {
	let bindings = vm.$options.routePaginationBindings

	if (typeof bindings === 'function') {
		bindings = bindings.call(vm)
	}

	if (Array.isArray(bindings) && bindings.length) {
		return bindings.filter(binding => binding && (binding.stateKey || binding.pageStateKey))
	}

	if (source?.options && typeof source.options === 'object' && 'page' in source.options) {
		return [
			{
				stateKey: 'options',
				pageQueryKey: 'page',
				perPageQueryKey: 'per_page',
			},
		]
	}

	if (typeof source?.page !== 'undefined') {
		return [
			{
				pageStateKey: 'page',
				pageQueryKey: 'page',
			},
		]
	}

	return []
}

function getBindingPage(source, binding) {
	if (binding.stateKey) {
		return source?.[binding.stateKey]?.page
	}

	return source?.[binding.pageStateKey]
}

function getBindingPerPage(source, binding) {
	if (!binding.stateKey) {
		return undefined
	}

	return source?.[binding.stateKey]?.itemsPerPage
}

function applyBindingToSource(source, binding, query) {
	const pageKey = binding.pageQueryKey || 'page'
	const perPageKey = binding.perPageQueryKey || 'per_page'
	const currentPage = getBindingPage(source, binding)
	const currentPerPage = getBindingPerPage(source, binding)

	if (binding.stateKey && source?.[binding.stateKey]) {
		if (query[pageKey] !== undefined) {
			source[binding.stateKey].page = parsePageValue(query[pageKey], currentPage || 1)
		}

		if (query[perPageKey] !== undefined && 'itemsPerPage' in source[binding.stateKey]) {
			source[binding.stateKey].itemsPerPage = parsePerPageValue(query[perPageKey], currentPerPage)
		}
	}

	if (binding.pageStateKey && pageKey && source && binding.pageStateKey in source && query[pageKey] !== undefined) {
		source[binding.pageStateKey] = parsePageValue(query[pageKey], source[binding.pageStateKey] || 1)
	}

	if (source?.filters && pageKey === 'page' && 'page' in source.filters) {
		source.filters.page = getBindingPage(source, binding)
	}

	if (source?.filters && perPageKey === 'per_page' && 'per_page' in source.filters && binding.stateKey) {
		source.filters.per_page = getBindingPerPage(source, binding)
	}
}

export default {
	beforeCreate() {
		const route = router.currentRoute

		if (!isListRoute(route) || !isRoutePaginationTarget(this) || typeof this.$options.data !== 'function') {
			return
		}

		const originalData = this.$options.data

		this.$options.data = function routePaginationDataWrapper(...args) {
			const data = originalData.apply(this, args)
			const bindings = getBindings(this, data)

			bindings.forEach(binding => {
				applyBindingToSource(data, binding, route.query || {})
			})

			return data
		}
	},
	mounted() {
		if (!this.$isListRoutePaginationEnabled()) {
			return
		}

		this.$applyRoutePaginationQuery(this.$route.query)
		this.$nextTick(() => {
			this.$syncRoutePaginationQuery()
		})

		this.__routePaginationUnwatchers = [
			this.$watch(
				() => this.$route.query,
				query => {
					this.$applyRoutePaginationQuery(query)
				},
				{ deep: true },
			),
			this.$watch(
				() => this.$getRoutePaginationSnapshot(),
				() => {
					this.$syncRoutePaginationQuery()
				},
				{ deep: true },
			),
		]
	},
	beforeDestroy() {
		this.__routePaginationUnwatchers?.forEach(unwatch => unwatch())
	},
	methods: {
		$isListRoutePaginationEnabled() {
			return (
				isListRoute(this.$route) &&
				isRoutePaginationTarget(this) &&
				this.$getRoutePaginationBindings().length > 0
			)
		},
		$getRoutePaginationBindings() {
			return getBindings(this, this)
		},
		$getRoutePaginationSnapshot() {
			if (!this.$isListRoutePaginationEnabled()) {
				return []
			}

			return this.$getRoutePaginationBindings().map(binding => ({
				pageQueryKey: binding.pageQueryKey || 'page',
				perPageQueryKey: binding.perPageQueryKey || 'per_page',
				page: getBindingPage(this, binding),
				perPage: getBindingPerPage(this, binding),
			}))
		},
		$applyRoutePaginationQuery(query = {}) {
			if (!this.$isListRoutePaginationEnabled()) {
				return
			}

			this.__routePaginationApplying = true

			this.$getRoutePaginationBindings().forEach(binding => {
				applyBindingToSource(this, binding, query)
			})

			this.$nextTick(() => {
				this.__routePaginationApplying = false
			})
		},
		$syncRoutePaginationQuery() {
			if (!this.$isListRoutePaginationEnabled() || this.__routePaginationApplying) {
				return
			}

			const nextQuery = { ...this.$route.query }
			let hasChanges = false

			this.$getRoutePaginationBindings().forEach(binding => {
				const pageKey = binding.pageQueryKey || 'page'
				const perPageKey = binding.perPageQueryKey || 'per_page'
				const pageValue = getBindingPage(this, binding)
				const perPageValue = getBindingPerPage(this, binding)

				if (pageValue !== undefined && String(nextQuery[pageKey] || '') !== String(pageValue)) {
					nextQuery[pageKey] = String(pageValue)
					hasChanges = true
				}

				if (
					binding.stateKey &&
					perPageValue !== undefined &&
					String(nextQuery[perPageKey] || '') !== String(perPageValue)
				) {
					nextQuery[perPageKey] = String(perPageValue)
					hasChanges = true
				}
			})

			if (!hasChanges) {
				return
			}

			this.$router.replace({
				query: nextQuery,
			}).catch(() => {})
		},
	},
}
